import supabase from '../lib/supabase';

// Demo payment gateway configurations
const defaultGatewayConfigs = {
  stripe: {
    id: 'stripe',
    name: 'Stripe',
    description: 'Accept payments via credit cards, debit cards, and bank transfers globally',
    isEnabled: false,
    isLive: false,
    config: {
      publishable_key_test: '',
      secret_key_test: '',
      publishable_key_live: '',
      secret_key_live: '',
      webhook_endpoint: '',
      supported_currencies: ['USD', 'EUR', 'GBP', 'INR'],
      payment_methods: ['card', 'bank_transfer', 'apple_pay', 'google_pay']
    },
    fees: {
      domestic_cards: '2.9% + $0.30',
      international_cards: '3.9% + $0.30',
      bank_transfers: '0.8%'
    }
  },
  razorpay: {
    id: 'razorpay',
    name: 'Razorpay',
    description: 'Accept payments from Indian customers via UPI, net banking, cards, and wallets',
    isEnabled: false,
    isLive: false,
    config: {
      key_id_test: '',
      key_secret_test: '',
      key_id_live: '',
      key_secret_live: '',
      webhook_secret: '',
      supported_currencies: ['INR'],
      payment_methods: ['card', 'netbanking', 'upi', 'wallet', 'emi']
    },
    fees: {
      domestic_cards: '2% + GST',
      international_cards: '3% + GST',
      upi: '₹2 + GST',
      netbanking: '₹15 + GST',
      wallets: '2% + GST'
    }
  },
  paypal: {
    id: 'paypal',
    name: 'PayPal',
    description: 'Accept payments via PayPal accounts and credit cards',
    isEnabled: false,
    isLive: false,
    config: {
      client_id_sandbox: '',
      client_secret_sandbox: '',
      client_id_live: '',
      client_secret_live: '',
      webhook_id: '',
      supported_currencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
      payment_methods: ['paypal', 'card', 'bank_transfer']
    },
    fees: {
      domestic: '2.9% + $0.30',
      international: '4.4% + $0.30',
      micropayments: '5% + $0.05'
    }
  }
};

class PaymentGatewayService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  async checkConnection() {
    try {
      const { data, error } = await supabase.from('payment_gateways_2024').select('count', { count: 'exact' });
      this.isOnline = !error;
    } catch (error) {
      this.isOnline = false;
    }
  }

  async getAllGateways() {
    if (!this.isOnline) {
      return { data: Object.values(defaultGatewayConfigs), error: null };
    }

    try {
      const { data, error } = await supabase
        .from('payment_gateways_2024')
        .select('*')
        .order('name');

      if (data && data.length > 0) {
        return { data, error };
      } else {
        // Return default configurations if no data exists
        return { data: Object.values(defaultGatewayConfigs), error: null };
      }
    } catch (error) {
      return { data: Object.values(defaultGatewayConfigs), error: null };
    }
  }

  async getGateway(gatewayId) {
    if (!this.isOnline) {
      return { data: defaultGatewayConfigs[gatewayId], error: null };
    }

    try {
      const { data, error } = await supabase
        .from('payment_gateways_2024')
        .select('*')
        .eq('id', gatewayId)
        .single();

      return { data: data || defaultGatewayConfigs[gatewayId], error };
    } catch (error) {
      return { data: defaultGatewayConfigs[gatewayId], error: null };
    }
  }

  async updateGateway(gatewayId, config) {
    if (!this.isOnline) {
      // Update local config for demo
      if (defaultGatewayConfigs[gatewayId]) {
        defaultGatewayConfigs[gatewayId] = { ...defaultGatewayConfigs[gatewayId], ...config };
        return { data: defaultGatewayConfigs[gatewayId], error: null };
      }
      return { data: null, error: 'Gateway not found' };
    }

    try {
      const { data, error } = await supabase
        .from('payment_gateways_2024')
        .upsert({
          id: gatewayId,
          ...config,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      // Fallback to local update
      if (defaultGatewayConfigs[gatewayId]) {
        defaultGatewayConfigs[gatewayId] = { ...defaultGatewayConfigs[gatewayId], ...config };
        return { data: defaultGatewayConfigs[gatewayId], error: null };
      }
      return { data: null, error: 'Failed to update gateway' };
    }
  }

  async testGatewayConnection(gatewayId, config) {
    // Simulate API test calls
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock test results
    const mockResults = {
      stripe: { success: true, message: 'Connection successful' },
      razorpay: { success: true, message: 'API keys validated' },
      paypal: { success: true, message: 'PayPal connection verified' }
    };

    return mockResults[gatewayId] || { success: false, message: 'Unknown gateway' };
  }

  generatePaymentLink(invoice, gatewayId = 'stripe') {
    const baseUrl = window.location.origin;
    const paymentId = `pay_${Date.now()}`;
    
    // In a real application, this would create actual payment links via gateway APIs
    const mockPaymentLinks = {
      stripe: `https://checkout.stripe.com/pay/${paymentId}`,
      razorpay: `https://rzp.io/l/${paymentId}`,
      paypal: `https://paypal.me/yourbusiness/${invoice.total_amount}`
    };

    return {
      payment_id: paymentId,
      payment_url: mockPaymentLinks[gatewayId] || `${baseUrl}/pay/${paymentId}`,
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
      gateway: gatewayId,
      invoice_id: invoice.id,
      amount: invoice.total_amount,
      currency: 'USD' // or INR for Razorpay
    };
  }

  async createSubscription(client, plan, gatewayId = 'stripe') {
    // Mock subscription creation
    const subscriptionId = `sub_${Date.now()}`;
    
    return {
      subscription_id: subscriptionId,
      client_id: client.id,
      plan_id: plan.id,
      gateway: gatewayId,
      status: 'active',
      current_period_start: new Date().toISOString(),
      current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    };
  }

  async processWebhook(gatewayId, payload, signature) {
    // Mock webhook processing
    console.log(`Processing ${gatewayId} webhook:`, payload);
    
    // In a real application, this would verify webhook signatures and process events
    return {
      processed: true,
      event_type: payload.type || 'payment.completed',
      timestamp: new Date().toISOString()
    };
  }

  async getPaymentMethods(gatewayId) {
    const gateway = defaultGatewayConfigs[gatewayId];
    if (!gateway) return { data: [], error: 'Gateway not found' };

    const paymentMethodDetails = {
      card: {
        id: 'card',
        name: 'Credit/Debit Card',
        description: 'Visa, Mastercard, American Express, and more',
        icon: 'FiCreditCard',
        processing_time: 'Instant'
      },
      bank_transfer: {
        id: 'bank_transfer',
        name: 'Bank Transfer',
        description: 'Direct bank account transfers',
        icon: 'FiSend',
        processing_time: '1-3 business days'
      },
      upi: {
        id: 'upi',
        name: 'UPI',
        description: 'Pay using any UPI app',
        icon: 'FiSmartphone',
        processing_time: 'Instant'
      },
      netbanking: {
        id: 'netbanking',
        name: 'Net Banking',
        description: 'Pay directly from your bank account',
        icon: 'FiGlobe',
        processing_time: 'Instant'
      },
      wallet: {
        id: 'wallet',
        name: 'Digital Wallets',
        description: 'Paytm, PhonePe, Google Pay, and more',
        icon: 'FiWallet',
        processing_time: 'Instant'
      },
      paypal: {
        id: 'paypal',
        name: 'PayPal',
        description: 'Pay with your PayPal account',
        icon: 'FiDollarSign',
        processing_time: 'Instant'
      },
      apple_pay: {
        id: 'apple_pay',
        name: 'Apple Pay',
        description: 'Pay with Touch ID or Face ID',
        icon: 'FiSmartphone',
        processing_time: 'Instant'
      },
      google_pay: {
        id: 'google_pay',
        name: 'Google Pay',
        description: 'Pay with Google Pay',
        icon: 'FiSmartphone',
        processing_time: 'Instant'
      }
    };

    const methods = gateway.config.payment_methods.map(methodId => 
      paymentMethodDetails[methodId]
    ).filter(Boolean);

    return { data: methods, error: null };
  }

  async getTransactionHistory(gatewayId, startDate, endDate) {
    // Mock transaction data
    const mockTransactions = [
      {
        id: 'txn_001',
        payment_id: 'pay_001',
        amount: 2500.00,
        currency: gatewayId === 'razorpay' ? 'INR' : 'USD',
        status: 'completed',
        payment_method: 'card',
        created_at: '2024-01-15T10:30:00Z',
        invoice_id: 'INV-001',
        client_name: 'TechCorp Ltd'
      },
      {
        id: 'txn_002',
        payment_id: 'pay_002',
        amount: 1800.00,
        currency: gatewayId === 'razorpay' ? 'INR' : 'USD',
        status: 'completed',
        payment_method: gatewayId === 'razorpay' ? 'upi' : 'card',
        created_at: '2024-01-14T14:20:00Z',
        invoice_id: 'INV-002',
        client_name: 'StartupXYZ'
      }
    ];

    return { data: mockTransactions, error: null };
  }

  async getAnalytics(gatewayId, period = '30d') {
    // Mock analytics data
    const analytics = {
      total_transactions: 156,
      total_volume: gatewayId === 'razorpay' ? 2540000 : 28500,
      currency: gatewayId === 'razorpay' ? 'INR' : 'USD',
      success_rate: 94.2,
      average_transaction_value: gatewayId === 'razorpay' ? 16282 : 182.69,
      top_payment_methods: gatewayId === 'razorpay' 
        ? [
            { method: 'UPI', percentage: 45 },
            { method: 'Cards', percentage: 35 },
            { method: 'Net Banking', percentage: 20 }
          ]
        : [
            { method: 'Credit Card', percentage: 65 },
            { method: 'Debit Card', percentage: 25 },
            { method: 'Bank Transfer', percentage: 10 }
          ],
      monthly_trend: [
        { month: 'Jan', volume: gatewayId === 'razorpay' ? 180000 : 2000 },
        { month: 'Feb', volume: gatewayId === 'razorpay' ? 220000 : 2400 },
        { month: 'Mar', volume: gatewayId === 'razorpay' ? 285000 : 3100 }
      ]
    };

    return { data: analytics, error: null };
  }
}

export default new PaymentGatewayService();